import { supabase } from '../supabase.js';
import { esc, fmtDate, KIND_LABEL, sectionBlocks, blocksHtml } from '../lib/report-render.js';
import { chartSpecs, mountCharts, chartPng } from '../lib/report-charts.js';

/* ============================================================
   Admin › Reports (docs/reports.md). Paymaster status reports,
   built section by section, cumulative as at a date.
   Two tabs:
   · Reports — the list; open one to generate, edit, ask for a
     correction, approve each section, then preview/print or
     compile the paymaster's Word form with the graphs.
   · Set-up  — the funding agreement the reports draw on: partner
     details, approved numbers, project value, accreditation,
     signatories, programmes' NQF/credits, the paymaster's
     payments, milestones, employer placements, the Word form.
   Figures come from report_data(); the agent (api/report-section)
   only writes the prose around them.
============================================================ */

const STATUS = {
  empty:            ['Not started', 'badge-gray'],
  draft:            ['Draft', 'badge-blue'],
  needs_correction: ['Needs correction', 'badge-red'],
  approved:         ['Approved', 'badge-green'],
};
const KIND_BADGE = { profile: 'badge-gray', data: 'badge-blue', narrative: 'badge-orange', chart: 'badge-blue' };
const METRICS = [
  ['', 'Typed by hand'], ['enrolled', 'Learners enrolled'], ['retained', 'Learners retained (active)'],
  ['completed', 'Learners completed'], ['left', 'Learners who left'], ['placed', 'Learners placed with employers'],
  ['paid_amount', 'Paid by the paymaster (R)'], ['stipend_gross', 'Stipends calculated (R)'],
];

const S = {
  tab: 'reports',
  agreements: [], templates: new Map(), programmes: [], reports: [],
  agreementId: null,
  report: null, sections: new Map(), figures: null,
  charts: new Map(),          // section key → Chart[]
  panel: null,                // { key, mode: 'edit' | 'correct' | 'history', versions? }
  busy: new Map(),            // section key → status text
  ledgers: {},
  runAll: null,               // progress text while "Generate all" runs
};

const opts = (list, val) => list.map(([v, l]) => `<option value="${esc(v)}" ${String(v) === String(val ?? '') ? 'selected' : ''}>${esc(l)}</option>`).join('');
const today = () => new Date().toISOString().slice(0, 10);

export async function renderReports() {
  const el = document.getElementById('view-reports');
  if (!window.__access?.parts?.includes('reports')) {
    el.innerHTML = `<div class="page-header"><h1>Reports</h1><p>Reports is not switched on for you. Ask an admin to switch it on under People &amp; rights.</p></div>`;
    return;
  }
  el.innerHTML = `<div class="page-header"><h1>Reports</h1><p>Loading…</p></div>`;
  try { S.tab = localStorage.getItem('rp-tab') || 'reports'; } catch { /* private window */ }
  await loadBase();
  let openId = null;
  try { openId = sessionStorage.getItem('rp-open'); } catch { /* ignore */ }
  if (S.tab === 'reports' && openId && S.reports.some((r) => r.id === openId)) await openReport(openId);
  else S.report = null;
  draw(el);
}

/* ═══════════════════════════ data ═══════════════════════════ */

async function loadBase() {
  const [tpl, fa, rep, prog] = await Promise.all([
    supabase.from('report_templates').select('template_key,name,paymaster,sections,docx_path,updated_at'),
    supabase.from('funding_agreements').select('*').order('is_default', { ascending: false }).order('name'),
    supabase.from('reports').select('id,title,as_at,status,agreement_id,template_key,compiled_path,compiled_at,updated_at,report_sections(status)').order('as_at', { ascending: false }).order('created_at', { ascending: false }),
    supabase.from('programmes').select('programme_id,programme_name,nqf_level,credits').order('programme_name'),
  ]);
  for (const r of [tpl, fa, rep, prog]) if (r.error) throw new Error(r.error.message);
  S.templates = new Map(tpl.data.map((t) => [t.template_key, t]));
  S.agreements = fa.data;
  S.reports = rep.data;
  S.programmes = prog.data;
  if (!S.agreementId || !S.agreements.some((a) => a.id === S.agreementId)) S.agreementId = S.agreements[0]?.id ?? null;
}

async function openReport(id) {
  const [{ data: report, error }, { data: secs, error: sErr }] = await Promise.all([
    supabase.from('reports').select('*').eq('id', id).single(),
    supabase.from('report_sections').select('*').eq('report_id', id),
  ]);
  if (error) throw new Error(error.message);
  if (sErr) throw new Error(sErr.message);
  S.report = report;
  S.sections = new Map(secs.map((s) => [s.section_key, s]));
  S.panel = null;
  try { sessionStorage.setItem('rp-open', id); } catch { /* ignore */ }
}

const template = () => S.templates.get(S.report?.template_key) || null;
const defs = () => template()?.sections || [];
const defOf = (key) => defs().find((d) => d.key === key);
const agreement = (id = S.report?.agreement_id) => S.agreements.find((a) => a.id === id) || null;

async function token() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || '';
}

async function figures() {
  const { data, error } = await supabase.rpc('report_data', { p_agreement_id: S.report.agreement_id, p_as_at: S.report.as_at });
  if (error) throw new Error(error.message);
  return data;
}

async function saveSection(key, patch) {
  const { data, error } = await supabase.from('report_sections')
    .upsert({ report_id: S.report.id, section_key: key, ...patch }).select().single();
  if (error) throw new Error(error.message);
  S.sections.set(key, data);
  return data;
}

/** Fill one section: the agent for written ones, report_data() for figures, Set-up for typed details. */
async function generate(key, { note = null, figs = null } = {}) {
  const def = defOf(key);
  if (def.prompt) {
    const resp = await fetch('/api/report-section', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await token()}` },
      body: JSON.stringify({ report_id: S.report.id, section_key: key, note }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(body.error || `The writer failed (${resp.status})`);
    S.sections.set(key, body.section);
    return;
  }
  let content;
  if (def.kind === 'profile' && def.block === 'signatures') {
    const named = new Map((agreement()?.signatories || []).map((s) => [s.role, s.name]));
    content = { rows: (def.roles || []).map((role) => ({ role, name: named.get(role) || '', date: '' })) };
  } else if (def.kind === 'profile') {
    content = { fields: { ...(agreement()?.profile || {}) } };
  } else {
    const f = figs || await figures();
    content = { data: f[def.block], as_at: S.report.as_at };
  }
  await saveSection(key, { content, status: 'draft', source: 'generated', correction_note: note, approved_at: null, approved_by: null });
}

/* ═══════════════════════════ layout ═══════════════════════════ */

function draw(el) {
  destroyCharts();
  el.innerHTML = `
    ${STYLE}
    <div class="ld-tabs" role="tablist" style="margin-top:4px">
      <button class="ld-tab ${S.tab === 'reports' ? 'on' : ''}" data-tab="reports" role="tab" aria-selected="${S.tab === 'reports'}">Reports</button>
      <button class="ld-tab ${S.tab === 'setup' ? 'on' : ''}" data-tab="setup" role="tab" aria-selected="${S.tab === 'setup'}">Set-up</button>
    </div>
    <div id="rp-body"></div>`;
  el.querySelectorAll('.ld-tab').forEach((b) => b.addEventListener('click', async () => {
    S.tab = b.dataset.tab;
    try { localStorage.setItem('rp-tab', S.tab); } catch { /* ignore */ }
    if (S.tab === 'setup') { S.report = null; try { sessionStorage.removeItem('rp-open'); } catch { /* ignore */ } }
    draw(el);
  }));
  const body = el.querySelector('#rp-body');
  if (S.tab === 'setup') {
    drawSetup(body).catch((err) => { body.innerHTML = `<div class="page-header"><h1>Set-up</h1><p class="ld-msg err">${esc(err.message)}</p></div>`; });
  } else if (S.report) drawReport(body);
  else drawList(body);
}

/* ═══════════════════════════ the list ═══════════════════════════ */

function drawList(body) {
  const fa = agreement(S.agreementId);
  const asAt = fa?.end_date && fa.end_date < today() ? fa.end_date : today();
  body.innerHTML = `
    <div class="page-header page-header-row">
      <div>
        <h1>Reports</h1>
        <p>Status reports for the paymaster, cumulative as at a date. Each section is generated from the live figures,
           checked and approved by management, then compiled into the paymaster's form with the graphs.</p>
      </div>
      <button class="btn btn-primary" id="rp-new-btn" ${S.agreements.length ? '' : 'disabled title="Add a funding agreement on Set-up first"'}>New report</button>
    </div>
    <div class="ld-card" id="rp-new" hidden>
      <h2>New report</h2>
      <p class="ld-sub">Pick the funding agreement and the date the report is "as at". Every figure is counted up to that date.</p>
      <div class="ld-grid">
        <label>Funding agreement
          <select id="rp-new-fa">${opts(S.agreements.map((a) => [a.id, a.name]), S.agreementId)}</select></label>
        <label>As at
          <input type="date" id="rp-new-asat" value="${asAt}"></label>
        <label style="grid-column:span 2">Title
          <input type="text" id="rp-new-title" value="${esc(defaultTitle(fa, asAt))}"></label>
      </div>
      <div class="ld-actions">
        <button class="btn btn-primary" id="rp-create">Create report</button>
        <button class="btn btn-ghost" id="rp-cancel">Cancel</button>
        <span class="ld-msg" id="rp-new-msg"></span>
      </div>
    </div>
    ${S.reports.length ? `
      <div class="table-wrap ld-card" style="padding:0">
        <table class="data-table">
          <thead><tr><th>Report</th><th>As at</th><th>Sections approved</th><th>Status</th><th>Last change</th></tr></thead>
          <tbody>${S.reports.map((r) => {
            const total = (S.templates.get(r.template_key)?.sections || []).length;
            const ok = (r.report_sections || []).filter((s) => s.status === 'approved').length;
            return `<tr class="clickable-row" data-open="${r.id}" tabindex="0">
              <td><b>${esc(r.title)}</b><div class="ld-small">${esc(agreement(r.agreement_id)?.name || '')}</div></td>
              <td>${fmtDate(r.as_at)}</td>
              <td>${ok} of ${total}</td>
              <td>${r.status === 'final' ? '<span class="badge badge-green">Final</span>' : '<span class="badge badge-gray">Draft</span>'}${r.compiled_at ? ` <span class="ld-small">compiled ${fmtDate(r.compiled_at)}</span>` : ''}</td>
              <td class="ld-small">${fmtDate(r.updated_at)}</td></tr>`;
          }).join('')}</tbody>
        </table>
      </div>` : `
      <div class="ld-card no-data">
        <p><b>No reports yet.</b></p>
        <p>Check the funding agreement on the Set-up tab (approved beneficiaries, project value, payments), then create the first report.</p>
      </div>`}`;

  const panel = body.querySelector('#rp-new');
  body.querySelector('#rp-new-btn').addEventListener('click', () => { panel.hidden = false; body.querySelector('#rp-new-title').focus(); });
  body.querySelector('#rp-cancel').addEventListener('click', () => { panel.hidden = true; });
  const retitle = () => {
    body.querySelector('#rp-new-title').value = defaultTitle(agreement(Number(body.querySelector('#rp-new-fa').value)), body.querySelector('#rp-new-asat').value);
  };
  body.querySelector('#rp-new-fa').addEventListener('change', retitle);
  body.querySelector('#rp-new-asat').addEventListener('change', retitle);
  body.querySelector('#rp-create').addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const msg = body.querySelector('#rp-new-msg');
    const fa2 = agreement(Number(body.querySelector('#rp-new-fa').value));
    const asAt2 = body.querySelector('#rp-new-asat').value;
    const title = body.querySelector('#rp-new-title').value.trim();
    if (!fa2 || !asAt2 || !title) { msg.className = 'ld-msg err'; msg.textContent = 'Pick an agreement, a date and a title.'; return; }
    btn.disabled = true; btn.textContent = 'Creating…';
    const { data, error } = await supabase.from('reports')
      .insert({ agreement_id: fa2.id, template_key: fa2.template_key, title, as_at: asAt2 }).select('id').single();
    if (error) { btn.disabled = false; btn.textContent = 'Create report'; msg.className = 'ld-msg err'; msg.textContent = error.message; return; }
    await loadBase();
    await openReport(data.id);
    draw(document.getElementById('view-reports'));
  });
  body.querySelectorAll('[data-open]').forEach((tr) => {
    const go = async () => { tr.style.opacity = '.6'; await openReport(tr.dataset.open); draw(document.getElementById('view-reports')); };
    tr.addEventListener('click', go);
    tr.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  });
}

function defaultTitle(fa, asAt) {
  const t = S.templates.get(fa?.template_key);
  return `${t?.name || 'Status report'} — as at ${fmtDate(asAt)}`;
}

/* ═══════════════════════════ one report ═══════════════════════════ */

function drawReport(body) {
  const r = S.report;
  const all = defs();
  const approved = all.filter((d) => S.sections.get(d.key)?.status === 'approved').length;
  const tpl = template();
  body.innerHTML = `
    <a href="#/reports" class="rp-back" id="rp-back">← Back to all reports</a>
    <div class="page-header page-header-row" style="margin-top:6px">
      <div>
        <h1>${esc(r.title)}</h1>
        <p>${esc(agreement()?.name || '')} · cumulative as at <b>${fmtDate(r.as_at)}</b> ·
           ${approved} of ${all.length} sections approved
           ${r.status === 'final' ? ' · <span class="badge badge-green">Final</span>' : ''}</p>
      </div>
      <div class="rp-head-actions">
        <button class="btn btn-ghost" id="rp-gen-all" ${S.runAll ? 'disabled' : ''}>${S.runAll ? esc(S.runAll) : 'Generate all not started'}</button>
        <button class="btn btn-ghost" id="rp-print">Preview &amp; print</button>
        <button class="btn btn-primary" id="rp-docx" ${tpl?.docx_path ? '' : 'disabled title="Upload the paymaster\'s Word form on the Set-up tab first"'}>Compile Word form</button>
      </div>
    </div>
    <div class="rp-progress" aria-hidden="true"><span style="width:${all.length ? (100 * approved) / all.length : 0}%"></span></div>
    <p class="ld-msg" id="rp-head-msg"></p>
    ${r.compiled_path ? `<p class="ld-small" style="margin:-4px 0 14px">Last compiled ${fmtDate(r.compiled_at)} · <a href="#" id="rp-last">download that copy</a></p>` : ''}

    <details class="ld-card rp-notes" ${r.notes ? '' : 'open'}>
      <summary><b>Notes for the writer</b> <span class="ld-small">${r.notes ? 'what management told the agent' : 'add context the figures cannot show'}</span></summary>
      <p class="ld-sub" style="margin-top:8px">Events, success stories, risks, steps already taken — the agent uses these alongside the figures and never invents its own.
         Regenerate a section after changing them.</p>
      <textarea id="rp-notes" rows="5" placeholder="e.g. Strike in Mnquma in February cost four training days. 12 learners from Kouga were offered work at a poultry farm.">${esc(r.notes || '')}</textarea>
      <div class="ld-actions" style="margin-top:8px;padding-top:8px"><button class="btn btn-ghost" id="rp-notes-save">Save notes</button><span class="ld-msg" id="rp-notes-msg"></span></div>
    </details>

    <div id="rp-sections">${all.map((d, i) => sectionHtml(d, i)).join('')}</div>
    <div class="ld-actions" style="justify-content:space-between">
      <span class="ld-small">Deleting a report removes its sections and history. The figures and Set-up stay.</span>
      <button class="btn btn-danger" id="rp-delete">Delete this report</button>
    </div>`;

  all.forEach((d) => wireSection(body, d.key));

  body.querySelector('#rp-back').addEventListener('click', (e) => {
    e.preventDefault();
    S.report = null;
    try { sessionStorage.removeItem('rp-open'); } catch { /* ignore */ }
    loadBase().then(() => draw(document.getElementById('view-reports')));
  });
  body.querySelector('#rp-notes-save').addEventListener('click', async (e) => {
    const msg = body.querySelector('#rp-notes-msg');
    e.currentTarget.disabled = true;
    const notes = body.querySelector('#rp-notes').value.trim() || null;
    const { error } = await supabase.from('reports').update({ notes }).eq('id', r.id);
    e.currentTarget.disabled = false;
    if (error) { msg.className = 'ld-msg err'; msg.textContent = error.message; return; }
    r.notes = notes;
    msg.className = 'ld-msg ok'; msg.textContent = 'Saved. Regenerate the written sections to use them.';
  });
  body.querySelector('#rp-gen-all').addEventListener('click', () => generateAll(body));
  body.querySelector('#rp-print').addEventListener('click', () => previewAndPrint());
  body.querySelector('#rp-docx').addEventListener('click', (e) => compileWord(e.currentTarget, body));
  body.querySelector('#rp-last')?.addEventListener('click', async (e) => {
    e.preventDefault();
    const { data, error } = await supabase.storage.from('documents').createSignedUrl(r.compiled_path, 120, { download: true });
    if (error) { headMsg(body, error.message, 'err'); return; }
    window.location.href = data.signedUrl;
  });
  body.querySelector('#rp-delete').addEventListener('click', async () => {
    if (!confirm(`Delete "${r.title}"? Its sections, their history and its compiled copies go with it.`)) return;
    const { data: files } = await supabase.storage.from('documents').list(`reports/${r.id}`);
    if (files?.length) await supabase.storage.from('documents').remove(files.map((f) => `reports/${r.id}/${f.name}`));
    const { error } = await supabase.from('reports').delete().eq('id', r.id);
    if (error) { headMsg(body, error.message, 'err'); return; }
    S.report = null;
    try { sessionStorage.removeItem('rp-open'); } catch { /* ignore */ }
    await loadBase();
    draw(document.getElementById('view-reports'));
  });
}

function headMsg(body, text, kind = 'ok') {
  const m = body.querySelector('#rp-head-msg');
  if (m) { m.className = `ld-msg ${kind}`; m.textContent = text; }
}

function sectionHtml(def, i) {
  const s = S.sections.get(def.key);
  const status = s?.status || 'empty';
  const [label, cls] = STATUS[status];
  const busy = S.busy.get(def.key);
  const content = s?.content;
  const blocks = sectionBlocks(def, content);
  const isOpen = S.panel?.key === def.key;
  const editable = def.kind === 'profile' || def.prompt;
  const genLabel = def.prompt ? (content ? 'Write again' : 'Write with the agent')
    : def.kind === 'profile' ? (content ? 'Fill again from Set-up' : 'Fill from Set-up')
    : (content ? 'Refresh figures' : 'Get figures');

  return `
    <section class="rp-sec rp-${status}" id="sec-${def.key}">
      <header>
        <span class="rp-num">${i + 1}</span>
        <h3>${esc(def.title)}</h3>
        <span class="badge ${KIND_BADGE[def.kind] || 'badge-gray'}">${esc(def.prompt && def.kind !== 'narrative' ? `${KIND_LABEL[def.kind]} + agent` : KIND_LABEL[def.kind])}</span>
        <span class="badge ${cls}">${label}</span>
        ${s?.version ? `<span class="ld-small">v${s.version} · ${esc(sourceWord(s.source))} ${fmtDate(s.updated_at)}</span>` : ''}
      </header>
      ${status === 'needs_correction' && s.correction_note ? `<div class="rp-flag"><b>Correction asked:</b> ${esc(s.correction_note)}
         <div class="ld-small">${def.prompt ? 'Write it again to apply the correction.' : 'Fix the source (Set-up tab or the learner records), then refresh the figures.'}</div></div>` : ''}
      ${(content?.warnings || []).map((w) => `<div class="rp-warn">${esc(w)}</div>`).join('')}
      <div class="rp-sec-body">
        ${busy ? `<p class="rp-busy"><span class="rp-spin"></span>${esc(busy)}</p>` : ''}
        ${content ? blocksHtml(blocks, (block) => chartsSlot(block, content.data))
          : `<p class="rp-empty">${def.prompt ? 'Not written yet. The agent drafts it from the figures and your notes.' : def.kind === 'profile' ? 'Not filled yet. It copies the details from Set-up; you can then edit them for this report.' : 'No figures yet. They are counted as at the report date.'}</p>`}
        ${content?.as_at && content.as_at !== S.report.as_at ? `<div class="rp-warn">These figures were taken as at ${fmtDate(content.as_at)} — refresh them.</div>` : ''}
      </div>
      <div class="rp-sec-actions">
        ${status !== 'approved' ? `<button class="btn ${content ? 'btn-ghost' : 'btn-primary'}" data-act="generate" ${busy ? 'disabled' : ''}>${genLabel}</button>` : ''}
        ${content && status !== 'approved' && editable ? `<button class="btn btn-ghost" data-act="edit" ${busy ? 'disabled' : ''}>Edit</button>` : ''}
        ${content && status !== 'approved' && def.kind !== 'profile' ? `<button class="btn btn-ghost" data-act="correct" ${busy ? 'disabled' : ''}>Request a correction</button>` : ''}
        ${content && status !== 'approved' ? `<button class="btn btn-ghost rp-approve" data-act="approve" ${busy ? 'disabled' : ''}>Approve</button>` : ''}
        ${status === 'approved' ? `<span class="ld-small">Approved ${fmtDate(s.approved_at)}</span><button class="btn btn-ghost" data-act="reopen">Reopen</button>` : ''}
        ${s?.version ? `<button class="btn btn-ghost" data-act="history">History</button>` : ''}
        <span class="ld-msg" data-msg></span>
      </div>
      ${isOpen ? `<div class="rp-panel">${panelHtml(def, s)}</div>` : ''}
    </section>`;
}

const sourceWord = (src) => ({ generated: 'generated', edited: 'edited', corrected: 'corrected', restored: 'restored' }[src] || 'saved');

function chartsSlot(block, data) {
  const specs = chartSpecs(block, data);
  return `<div class="rp-charts">${specs.map((s) => `
    <figure class="rp-chart${s.wide ? ' rp-wide' : ''}"><figcaption>${esc(s.title)}</figcaption>
      <div style="position:relative;height:${s.height}px"><canvas data-chart="${s.id}" role="img" aria-label="${esc(s.title)}"></canvas></div>
    </figure>`).join('')}</div>`;
}

function panelHtml(def, s) {
  const mode = S.panel.mode;
  const c = s?.content || {};
  if (mode === 'correct') {
    return `
      <label class="rp-label">What should change?
        <textarea data-f="note" rows="3" placeholder="${def.prompt ? 'e.g. Mention the February strike in Mnquma, and keep it to one paragraph.' : 'e.g. Approved beneficiaries should be 3 000, not 2 996.'}"></textarea></label>
      <p class="ld-small">${def.prompt ? 'The agent rewrites this section to apply it. The current version stays in History.'
        : 'Figures come from the system, so the section is marked for correction. Fix the source, then refresh the figures.'}</p>
      <div class="ld-actions" style="margin-top:8px;padding-top:8px">
        <button class="btn btn-primary" data-act="send-correction">${def.prompt ? 'Rewrite with this correction' : 'Mark for correction'}</button>
        <button class="btn btn-ghost" data-act="close">Cancel</button></div>`;
  }
  if (mode === 'history') {
    const vs = S.panel.versions;
    if (!vs) return `<p class="rp-busy"><span class="rp-spin"></span>Loading history…</p>`;
    return `<table class="rp-table"><thead><tr><th>Version</th><th>What happened</th><th>When</th><th></th></tr></thead><tbody>
      ${vs.map((v) => `<tr><td>v${v.version}</td><td>${esc(sourceWord(v.source))}${v.note ? `<div class="ld-small">“${esc(v.note)}”</div>` : ''}${v.content?.text ? `<div class="ld-small rp-clip">${esc(v.content.text.slice(0, 160))}${v.content.text.length > 160 ? '…' : ''}</div>` : ''}</td>
        <td class="ld-small">${new Date(v.created_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}</td>
        <td>${v.version === s.version ? '<span class="ld-small">current</span>' : `<button class="btn btn-ghost" data-restore="${v.version}">Restore</button>`}</td></tr>`).join('')}
      </tbody></table>
      <div class="ld-actions" style="margin-top:8px;padding-top:8px"><button class="btn btn-ghost" data-act="close">Close</button></div>`;
  }
  // edit
  let fields = '';
  if (def.kind === 'profile' && def.block === 'signatures') {
    fields = `<table class="rp-table"><thead><tr><th>Role</th><th>Name and surname</th><th>Date</th></tr></thead><tbody>
      ${(c.rows || []).map((r, i) => `<tr><td>${esc(r.role)}</td><td><input data-sig-name="${i}" value="${esc(r.name)}"></td><td><input type="date" data-sig-date="${i}" value="${esc(r.date)}"></td></tr>`).join('')}
      </tbody></table>`;
  } else if (def.kind === 'profile') {
    fields = `<div class="ld-grid">${(def.fields || []).map((f) => `<label>${esc(f.label)}${f.multiline
      ? `<textarea data-field="${f.key}" rows="3">${esc(c.fields?.[f.key] || '')}</textarea>`
      : `<input data-field="${f.key}" value="${esc(c.fields?.[f.key] || '')}">`}</label>`).join('')}</div>
      <p class="ld-small">Changes here apply to this report only. Set-up holds the details every new report starts from.</p>`;
  } else {
    fields = `<label class="rp-label">Text <textarea data-f="text" rows="${Math.min(18, Math.max(6, (c.text || '').split('\n').length + 3))}">${esc(c.text || '')}</textarea></label>
      <p class="ld-small">Blank line between paragraphs; start a line with "- " for a bullet. Figures you type are not checked against the system — keep them as given.</p>`;
    if (def.block === 'deliverables' && Array.isArray(c.data)) {
      fields += `<table class="rp-table"><thead><tr><th>Deliverable</th><th>Reason for variance</th></tr></thead><tbody>
        ${c.data.map((m) => `<tr><td>${esc(m.deliverable)}</td><td><textarea data-reason="${m.id}" rows="2">${esc(c.reasons?.[m.id] ?? m.reason ?? '')}</textarea></td></tr>`).join('')}</tbody></table>`;
    }
  }
  return `${fields}
    <div class="ld-actions" style="margin-top:10px;padding-top:10px">
      <button class="btn btn-primary" data-act="save">Save</button>
      <button class="btn btn-ghost" data-act="close">Cancel</button></div>`;
}

function redrawSection(key) {
  const node = document.getElementById(`sec-${key}`);
  if (!node) return;
  (S.charts.get(key) || []).forEach((c) => c.destroy());
  S.charts.delete(key);
  const i = defs().findIndex((d) => d.key === key);
  node.outerHTML = sectionHtml(defOf(key), i);
  wireSection(document.getElementById('rp-body'), key);
  refreshHeader();
}

/** The approved count and progress bar, without redrawing every section. */
function refreshHeader() {
  const all = defs();
  const approved = all.filter((d) => S.sections.get(d.key)?.status === 'approved').length;
  const bar = document.querySelector('.rp-progress span');
  if (bar) bar.style.width = `${all.length ? (100 * approved) / all.length : 0}%`;
  const p = document.querySelector('#rp-body .page-header p');
  if (p) p.innerHTML = p.innerHTML.replace(/\d+ of \d+ sections approved/, `${approved} of ${all.length} sections approved`);
}

function wireSection(body, key) {
  const node = body.querySelector(`#sec-${key}`);
  if (!node) return;
  const def = defOf(key);
  const s = S.sections.get(key);
  if (s?.content?.data && def.kind === 'chart') S.charts.set(key, mountCharts(node, chartSpecs(def.block, s.content.data)));
  const msg = (text, kind = 'err') => { const m = node.querySelector('[data-msg]'); if (m) { m.className = `ld-msg ${kind}`; m.textContent = text; } };

  const run = async (label, fn) => {
    S.busy.set(key, label);
    redrawSection(key);
    try { await fn(); S.busy.delete(key); redrawSection(key); }
    catch (err) { S.busy.delete(key); redrawSection(key); const n = document.getElementById(`sec-${key}`); const m = n?.querySelector('[data-msg]'); if (m) { m.className = 'ld-msg err'; m.textContent = err.message; } }
  };

  node.querySelectorAll('[data-act]').forEach((b) => b.addEventListener('click', async () => {
    const act = b.dataset.act;
    if (act === 'generate') {
      S.panel = null;
      return run(def.prompt ? 'The agent is writing this section — about 20–40 seconds…' : 'Counting…', () => generate(key));
    }
    if (act === 'edit' || act === 'correct') { S.panel = { key, mode: act }; return redrawSection(key); }
    if (act === 'close') { S.panel = null; return redrawSection(key); }
    if (act === 'history') {
      S.panel = { key, mode: 'history' };
      redrawSection(key);
      const { data, error } = await supabase.from('report_section_versions')
        .select('version,source,note,content,created_at').eq('report_id', S.report.id).eq('section_key', key).order('version', { ascending: false }).limit(30);
      if (error) return msg(error.message);
      if (S.panel?.key === key) { S.panel.versions = data; redrawSection(key); }
      return;
    }
    if (act === 'approve' || act === 'reopen') {
      const { data: u } = await supabase.auth.getUser();
      return run(act === 'approve' ? 'Approving…' : 'Reopening…', () => saveSection(key, act === 'approve'
        ? { status: 'approved', approved_at: new Date().toISOString(), approved_by: u?.user?.id ?? null }
        : { status: 'draft', approved_at: null, approved_by: null }));
    }
    if (act === 'send-correction') {
      const note = node.querySelector('[data-f="note"]').value.trim();
      if (!note) return msg('Say what should change.');
      S.panel = null;
      if (def.prompt) return run('The agent is applying your correction…', () => generate(key, { note }));
      return run('Saving…', () => saveSection(key, { status: 'needs_correction', correction_note: note }));
    }
    if (act === 'save') {
      const content = { ...(s?.content || {}) };
      if (def.kind === 'profile' && def.block === 'signatures') {
        content.rows = (content.rows || []).map((r, i) => ({ ...r,
          name: node.querySelector(`[data-sig-name="${i}"]`).value.trim(),
          date: node.querySelector(`[data-sig-date="${i}"]`).value }));
      } else if (def.kind === 'profile') {
        content.fields = { ...(content.fields || {}) };
        node.querySelectorAll('[data-field]').forEach((f) => { content.fields[f.dataset.field] = f.value.trim(); });
      } else {
        content.text = node.querySelector('[data-f="text"]').value.trim();
        const reasons = {};
        node.querySelectorAll('[data-reason]').forEach((t) => { if (t.value.trim()) reasons[t.dataset.reason] = t.value.trim(); });
        if (node.querySelector('[data-reason]')) content.reasons = reasons;
        content.warnings = [];   // a person's wording replaces the agent's
      }
      S.panel = null;
      return run('Saving…', () => saveSection(key, { content, status: 'draft', source: 'edited', correction_note: null }));
    }
  }));
  node.querySelectorAll('[data-restore]').forEach((b) => b.addEventListener('click', () => {
    const v = S.panel?.versions?.find((x) => x.version === Number(b.dataset.restore));
    if (!v) return;
    S.panel = null;
    run('Restoring…', () => saveSection(key, { content: v.content, status: 'draft', source: 'restored', correction_note: null, approved_at: null, approved_by: null }));
  }));
}

async function generateAll(body) {
  const todo = defs().filter((d) => (S.sections.get(d.key)?.status || 'empty') === 'empty');
  if (!todo.length) { headMsg(body, 'Every section has been started. Use each section’s own button to refresh or rewrite it.', 'warn'); return; }
  // figures and typed details first, so the agent's sections can read them
  const order = [...todo.filter((d) => !d.prompt), ...todo.filter((d) => d.prompt)];
  const btn = body.querySelector('#rp-gen-all');
  let figs = null;
  const failed = [];
  for (const [i, d] of order.entries()) {
    S.runAll = `${i + 1} of ${order.length}: ${d.title}…`;
    btn.textContent = S.runAll; btn.disabled = true;
    S.busy.set(d.key, d.prompt ? 'The agent is writing this section…' : 'Counting…');
    redrawSection(d.key);
    document.getElementById(`sec-${d.key}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    try {
      if (!d.prompt && d.kind !== 'profile' && !figs) figs = await figures();
      await generate(d.key, { figs });
    } catch (err) { failed.push(`${d.title}: ${err.message}`); }
    S.busy.delete(d.key);
    redrawSection(d.key);
  }
  S.runAll = null;
  btn.textContent = 'Generate all not started'; btn.disabled = false;
  headMsg(body, failed.length ? `Done, with ${failed.length} problem(s): ${failed.join(' · ')}` : 'Every section has a draft. Read each one, edit or ask for corrections, then approve.', failed.length ? 'err' : 'ok');
}

/* ═══════════════════════════ preview, print, Word ═══════════════════════════ */

async function annexureImages() {
  const out = [];
  for (const d of defs().filter((x) => x.kind === 'chart')) {
    const s = S.sections.get(d.key);
    if (!s?.content?.data) continue;
    const images = [];
    for (const spec of chartSpecs(d.block, s.content.data)) images.push({ title: spec.title, ...(await chartPng(spec)) });
    const blocks = sectionBlocks(d, s.content);
    const intro = [
      ...blocks.filter((b) => b.type === 'tiles').flatMap((b) => [b.tiles.map((t) => `${t.label}: ${t.value}`).join('   ·   ')]),
      ...blocks.filter((b) => b.type === 'note').map((b) => b.text),
    ];
    out.push({ key: d.key, title: d.title, intro, images, blocks });
  }
  return out;
}

async function previewAndPrint() {
  const win = window.open('', '_blank');
  if (!win) { alert('Allow pop-ups for this site to open the preview.'); return; }
  win.document.write('<p style="font-family:Inter,sans-serif;padding:24px">Preparing the report…</p>');
  const annex = await annexureImages();
  const r = S.report;
  const unapproved = defs().filter((d) => S.sections.get(d.key)?.status !== 'approved').length;
  const parts = defs().map((d, i) => {
    const s = S.sections.get(d.key);
    if (d.kind === 'chart') {
      const a = annex.find((x) => x.key === d.key);
      if (!a) return `<section><h2>${i + 1}. ${esc(d.title)}</h2><p class="rp-note">Not generated.</p></section>`;
      return `<section class="annex"><h2>${esc(d.title)}</h2>${blocksHtml(a.blocks, () => a.images.map((img) => `<figure><figcaption>${esc(img.title)}</figcaption><img src="${img.url}" alt="${esc(img.title)}"></figure>`).join(''))}</section>`;
    }
    return `<section><h2>${i + 1}. ${esc(d.title)}</h2>${s?.content ? blocksHtml(sectionBlocks(d, s.content)) : '<p class="rp-note">Not written yet.</p>'}</section>`;
  }).join('');
  win.document.open();
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.title)}</title>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
    <style>
      body { font-family: Inter, system-ui, sans-serif; color:#0F172A; max-width: 800px; margin: 24px auto; padding: 0 20px; font-size: 13px; line-height: 1.5; }
      h1 { font-size: 20px; margin: 0 0 4px; } h2 { font-size: 15px; margin: 22px 0 8px; border-bottom: 1px solid #E5E9EE; padding-bottom: 4px; }
      .meta { color:#64748B; margin: 0 0 18px; } .draft { color:#B45309; font-weight:600; }
      table { border-collapse: collapse; width: 100%; margin: 6px 0 10px; }
      th, td { border: 1px solid #CBD5E1; padding: 5px 7px; vertical-align: top; text-align: left; }
      .rp-kv th { width: 44%; background: #F8FAFC; font-weight: 600; }
      .rp-table th { background: #F8FAFC; font-size: 12px; }
      .rp-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 8px 0; }
      .rp-tile { border: 1px solid #E5E9EE; border-radius: 8px; padding: 8px; } .rp-tile span, .rp-tile small { display:block; color:#64748B; font-size: 11px; } .rp-tile b { font-size: 17px; }
      .rp-note { color:#64748B; font-style: italic; } .rp-blank { color:#94A3B8; }
      figure { margin: 12px 0; break-inside: avoid; } figcaption { font-weight: 600; margin-bottom: 4px; } img { width: 100%; }
      section.annex { break-before: page; }
      .bar { position: sticky; top: 0; background: #fff; padding: 8px 0; border-bottom: 1px solid #E5E9EE; margin-bottom: 12px; }
      .bar button { font: inherit; padding: 6px 14px; border-radius: 6px; border: 0; background: #F97316; color: #fff; font-weight: 600; cursor: pointer; }
      @media print { .bar { display: none; } body { margin: 0; } }
    </style></head><body>
    <div class="bar"><button onclick="window.print()">Print or save as PDF</button></div>
    <h1>${esc(r.title)}</h1>
    <p class="meta">${esc(agreement()?.name || '')} · cumulative as at ${fmtDate(r.as_at)}${unapproved ? ` · <span class="draft">DRAFT — ${unapproved} section(s) not approved</span>` : ''}</p>
    ${parts}
  </body></html>`);
  win.document.close();
}

async function compileWord(btn, body) {
  const tpl = template();
  const unapproved = defs().filter((d) => S.sections.get(d.key)?.status !== 'approved');
  if (unapproved.length && !confirm(`${unapproved.length} section(s) are not approved yet (${unapproved.slice(0, 4).map((d) => d.title).join(', ')}${unapproved.length > 4 ? '…' : ''}).\n\nCompile a DRAFT anyway?`)) return;
  const old = btn.textContent;
  btn.disabled = true;
  try {
    btn.textContent = 'Drawing the graphs…';
    const annex = await annexureImages();
    btn.textContent = 'Filling the form…';
    const { data: file, error } = await supabase.storage.from('documents').download(tpl.docx_path);
    if (error) throw new Error(`Could not read the Word form: ${error.message}`);
    const { compileDocx } = await import('../lib/report-docx.js');
    const blob = await compileDocx(await file.arrayBuffer(), {
      templateKey: S.report.template_key, defs: defs(), sections: S.sections,
      annexures: annex.map((a) => ({ title: a.title, intro: a.intro, images: a.images })),
    });
    const name = `${S.report.title.replace(/[^\w\- ]+/g, '').replace(/\s+/g, ' ').trim()}${unapproved.length ? ' DRAFT' : ''}.docx`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 30000);

    btn.textContent = 'Saving a copy…';
    const path = `reports/${S.report.id}/${name.replace(/\s+/g, '-')}`;
    const up = await supabase.storage.from('documents').upload(path, blob, { upsert: true, contentType: blob.type });
    if (up.error) throw new Error(`Downloaded, but the copy was not saved: ${up.error.message}`);
    const patch = { compiled_path: path, compiled_at: new Date().toISOString(), ...(unapproved.length ? {} : { status: 'final' }) };
    const { error: uErr } = await supabase.from('reports').update(patch).eq('id', S.report.id);
    if (uErr) throw new Error(uErr.message);
    Object.assign(S.report, patch);
    headMsg(body, unapproved.length ? 'Draft compiled and downloaded.' : 'Compiled and downloaded. Every section is approved, so the report is now final.', 'ok');
  } catch (err) {
    headMsg(body, err.message, 'err');
  } finally {
    btn.disabled = false; btn.textContent = old;
  }
}

function destroyCharts() {
  for (const list of S.charts.values()) list.forEach((c) => c.destroy());
  S.charts.clear();
}

/* ═══════════════════════════ Set-up ═══════════════════════════ */

const LEDGERS = {
  payments: {
    table: 'funding_payments', title: 'Payments from the paymaster',
    sub: 'Each invoice the paymaster has paid. The report shows the running balance against the project value.',
    order: (a, b) => (a.period_year - b.period_year) || (a.period_month - b.period_month),
    cols: [
      { key: 'ym', label: 'Month', type: 'month', required: true },
      { key: 'beneficiaries', label: 'Beneficiaries', type: 'number' },
      { key: 'amount', label: 'Amount (R)', type: 'number', required: true },
      { key: 'invoice_ref', label: 'Invoice no.', type: 'text' },
      { key: 'paid_on', label: 'Paid on', type: 'date' },
    ],
  },
  milestones: {
    table: 'funding_milestones', title: 'Deliverables and milestones',
    sub: 'From the implementation plan. Pick what the actual is measured by and the system fills it in; otherwise type it.',
    order: (a, b) => (a.sort - b.sort) || (a.id - b.id),
    cols: [
      { key: 'deliverable', label: 'Planned deliverable', type: 'text', required: true, wide: true },
      { key: 'due_date', label: 'Due', type: 'date' },
      { key: 'target', label: 'Target', type: 'number' },
      { key: 'metric', label: 'Actual measured by', type: 'select', options: METRICS },
      { key: 'actual_text', label: 'Actual (if typed)', type: 'text' },
      { key: 'reason', label: 'Reason for any variance', type: 'text', wide: true },
    ],
  },
  placements: {
    table: 'employer_placements', title: 'Employer placements (exit strategy)',
    sub: 'Employers that absorbed beneficiaries after training.',
    order: (a, b) => String(a.employer).localeCompare(String(b.employer)),
    cols: [
      { key: 'employer', label: 'Employer', type: 'text', required: true, wide: true },
      { key: 'address', label: 'Address', type: 'text' },
      { key: 'contact', label: 'Contact details', type: 'text' },
      { key: 'sector', label: 'Sector', type: 'text' },
      { key: 'absorbed', label: 'Absorbed', type: 'number' },
      { key: 'programme_id', label: 'Programme', type: 'select', options: null },
    ],
  },
};

async function loadLedgers(faId) {
  const res = await Promise.all(Object.entries(LEDGERS).map(([k, L]) =>
    supabase.from(L.table).select('*').eq('agreement_id', faId).then((r) => [k, r])));
  for (const [k, r] of res) {
    if (r.error) throw new Error(r.error.message);
    S.ledgers[k] = r.data.sort(LEDGERS[k].order).map((row) => ({ ...row,
      ...(k === 'payments' ? { ym: `${row.period_year}-${String(row.period_month).padStart(2, '0')}` } : {}) }));
  }
}

async function drawSetup(body) {
  const fa = agreement(S.agreementId);
  if (!fa) {
    body.innerHTML = `<div class="page-header"><h1>Set-up</h1></div><div class="ld-card no-data"><p>No funding agreement yet. Ask for one to be added.</p></div>`;
    return;
  }
  body.innerHTML = `<div class="page-header"><h1>Set-up</h1><p>Loading…</p></div>`;
  await loadLedgers(fa.id);
  const tpl = S.templates.get(fa.template_key);
  const partnerDef = (tpl?.sections || []).find((d) => d.key === 'partner');
  const sigDef = (tpl?.sections || []).find((d) => d.block === 'signatures');
  const named = new Map((fa.signatories || []).map((s) => [s.role, s.name]));
  const seta = (fa.seta || []).length ? fa.seta : [{ body: '', accreditation: '' }];

  body.innerHTML = `
    <div class="page-header page-header-row">
      <div><h1>Set-up</h1>
        <p>What every report on this funding agreement starts from. Changes here show in a report when its sections are refreshed.</p></div>
      ${S.agreements.length > 1 ? `<label class="rp-label" style="min-width:240px">Funding agreement
        <select id="rp-fa">${opts(S.agreements.map((a) => [a.id, a.name]), fa.id)}</select></label>` : ''}
    </div>

    <div class="ld-card" id="card-fa">
      <h2>Funding agreement</h2>
      <p class="ld-sub">${esc(tpl?.paymaster || '')} · ${esc(tpl?.name || '')}${fa.is_default ? ' · every class without its own agreement counts here' : ''}</p>
      <div class="ld-grid">
        <label>Name <input data-fa="name" value="${esc(fa.name)}"></label>
        <label>Funding agreement year <input data-fa="agreement_year" value="${esc(fa.agreement_year || '')}" placeholder="2025/26"></label>
        <label>Approved beneficiaries <input type="number" min="0" data-fa="approved_beneficiaries" value="${esc(fa.approved_beneficiaries ?? '')}"></label>
        <label>Project value (R) <input type="number" min="0" step="0.01" data-fa="project_value" value="${esc(fa.project_value ?? '')}"></label>
        <label>Training start <input type="date" data-fa="start_date" value="${esc(fa.start_date || '')}"></label>
        <label>Training end <input type="date" data-fa="end_date" value="${esc(fa.end_date || '')}"></label>
        <label>Beneficiaries registered with SETA / QCTO
          <select data-fa="registered_with_seta">${opts([['', 'Not set'], ['true', 'Yes'], ['false', 'No']], fa.registered_with_seta == null ? '' : String(fa.registered_with_seta))}</select></label>
      </div>
      <h3>Partner details</h3>
      <div class="ld-grid">${(partnerDef?.fields || []).map((f) => `<label>${esc(f.label)}${f.multiline
        ? `<textarea data-profile="${f.key}" rows="3">${esc(fa.profile?.[f.key] || '')}</textarea>`
        : `<input data-profile="${f.key}" value="${esc(fa.profile?.[f.key] || '')}">`}</label>`).join('')}</div>
      <h3>SETA / QCTO accreditation</h3>
      <div class="table-wrap"><table class="ld-table" id="rp-seta"><thead><tr><th>SETA name / QCTO</th><th>Accreditation number(s)</th><th></th></tr></thead>
        <tbody>${seta.map((s) => setaRow(s)).join('')}</tbody></table></div>
      <button class="btn btn-ghost" id="rp-seta-add" style="margin-top:6px">+ Add a SETA / QCTO line</button>
      ${sigDef ? `<h3>Signatories</h3>
        <div class="ld-grid">${sigDef.roles.map((role) => `<label>${esc(role)}<input data-sig="${esc(role)}" value="${esc(named.get(role) || '')}"></label>`).join('')}</div>` : ''}
      <div class="ld-actions"><button class="btn btn-primary" id="rp-fa-save">Save funding agreement</button><span class="ld-msg" id="rp-fa-msg"></span></div>
    </div>

    <div class="ld-card" id="card-prog">
      <h2>Programmes</h2>
      <p class="ld-sub">NQF level and credits print in the learning interventions table. Changing them needs Classes &amp; set-up.</p>
      <div class="table-wrap"><table class="ld-table"><thead><tr><th>Programme</th><th style="width:120px">NQF level</th><th style="width:120px">Credits</th></tr></thead>
        <tbody>${S.programmes.map((p) => `<tr data-prog="${p.programme_id}"><td>${esc(p.programme_name)}</td>
          <td><input data-k="nqf_level" value="${esc(p.nqf_level || '')}" placeholder="e.g. 2"></td>
          <td><input type="number" min="0" data-k="credits" value="${esc(p.credits ?? '')}"></td></tr>`).join('')}</tbody></table></div>
      <div class="ld-actions"><button class="btn btn-primary" id="rp-prog-save">Save programmes</button><span class="ld-msg" id="rp-prog-msg"></span></div>
    </div>

    ${Object.keys(LEDGERS).map((k) => `<div class="ld-card" id="card-${k}"></div>`).join('')}

    <div class="ld-card" id="card-docx">
      <h2>The paymaster's Word form</h2>
      <p class="ld-sub">Upload the blank form (.docx) as the paymaster issues it. "Compile Word form" fills it in and adds the graphs as annexures.</p>
      <p>${tpl?.docx_path ? `<span class="badge badge-green">Uploaded</span> <span class="ld-small">${esc(tpl.docx_path.split('/').pop())} · ${fmtDate(tpl.updated_at)}</span>` : '<span class="badge badge-orange">Not uploaded yet</span>'}</p>
      <div class="ld-actions" style="border-top:0;margin-top:4px;padding-top:0">
        <input type="file" id="rp-docx-file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document">
        <button class="btn btn-primary" id="rp-docx-up">Upload</button><span class="ld-msg" id="rp-docx-msg"></span></div>
    </div>`;

  Object.keys(LEDGERS).forEach((k) => drawLedger(body, k, fa.id));

  body.querySelector('#rp-fa')?.addEventListener('change', (e) => { S.agreementId = Number(e.target.value); drawSetup(body); });
  body.querySelector('#rp-seta-add').addEventListener('click', () => {
    body.querySelector('#rp-seta tbody').insertAdjacentHTML('beforeend', setaRow({ body: '', accreditation: '' }));
    wireSetaRemove(body);
  });
  wireSetaRemove(body);

  body.querySelector('#rp-fa-save').addEventListener('click', async (e) => {
    const btn = e.currentTarget; const msg = body.querySelector('#rp-fa-msg');
    const v = (k) => body.querySelector(`[data-fa="${k}"]`).value.trim();
    const num = (k) => (v(k) === '' ? null : Number(v(k)));
    const reg = v('registered_with_seta');
    const profile = { ...(fa.profile || {}) };
    body.querySelectorAll('[data-profile]').forEach((i) => { profile[i.dataset.profile] = i.value.trim(); });
    const patch = {
      name: v('name') || fa.name, agreement_year: v('agreement_year') || null,
      approved_beneficiaries: num('approved_beneficiaries'), project_value: num('project_value'),
      start_date: v('start_date') || null, end_date: v('end_date') || null,
      registered_with_seta: reg === '' ? null : reg === 'true',
      profile,
      seta: [...body.querySelectorAll('#rp-seta tbody tr')].map((tr) => ({
        body: tr.querySelector('[data-s="body"]').value.trim(), accreditation: tr.querySelector('[data-s="accreditation"]').value.trim() }))
        .filter((s) => s.body || s.accreditation),
      signatories: [...body.querySelectorAll('[data-sig]')].map((i) => ({ role: i.dataset.sig, name: i.value.trim() })).filter((s) => s.name),
    };
    btn.disabled = true; btn.textContent = 'Saving…';
    const { error } = await supabase.from('funding_agreements').update(patch).eq('id', fa.id);
    btn.disabled = false; btn.textContent = 'Save funding agreement';
    if (error) { msg.className = 'ld-msg err'; msg.textContent = error.message; return; }
    Object.assign(fa, patch);
    msg.className = 'ld-msg ok'; msg.textContent = 'Saved. Refresh the matching sections in open reports to pick it up.';
  });

  body.querySelector('#rp-prog-save').addEventListener('click', async (e) => {
    const btn = e.currentTarget; const msg = body.querySelector('#rp-prog-msg');
    const changed = [];
    body.querySelectorAll('[data-prog]').forEach((tr) => {
      const p = S.programmes.find((x) => x.programme_id === tr.dataset.prog);
      const nqf = tr.querySelector('[data-k="nqf_level"]').value.trim() || null;
      const cr = tr.querySelector('[data-k="credits"]').value.trim();
      const credits = cr === '' ? null : Number(cr);
      if (nqf !== (p.nqf_level || null) || credits !== (p.credits ?? null)) changed.push({ p, nqf_level: nqf, credits });
    });
    if (!changed.length) { msg.className = 'ld-msg warn'; msg.textContent = 'Nothing changed.'; return; }
    btn.disabled = true; btn.textContent = 'Saving…';
    const fails = [];
    for (const c of changed) {
      const { data, error } = await supabase.from('programmes').update({ nqf_level: c.nqf_level, credits: c.credits }).eq('programme_id', c.p.programme_id).select('programme_id');
      if (error || !data?.length) fails.push(c.p.programme_name);
      else Object.assign(c.p, { nqf_level: c.nqf_level, credits: c.credits });
    }
    btn.disabled = false; btn.textContent = 'Save programmes';
    msg.className = `ld-msg ${fails.length ? 'err' : 'ok'}`;
    msg.textContent = fails.length ? `Not saved: ${fails.join(', ')} — changing programmes needs Classes & set-up switched on.` : `Saved ${changed.length} programme(s).`;
  });

  body.querySelector('#rp-docx-up').addEventListener('click', async (e) => {
    const btn = e.currentTarget; const msg = body.querySelector('#rp-docx-msg');
    const file = body.querySelector('#rp-docx-file').files[0];
    if (!file) { msg.className = 'ld-msg err'; msg.textContent = 'Choose the .docx first.'; return; }
    btn.disabled = true; btn.textContent = 'Checking…';
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(await file.arrayBuffer()).catch(() => null);
      if (!zip?.file('word/document.xml')) throw new Error('That is not a Word (.docx) file.');
      btn.textContent = 'Uploading…';
      const path = `report-templates/${fa.template_key}.docx`;
      const up = await supabase.storage.from('documents').upload(path, file, { upsert: true, contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      if (up.error) throw new Error(up.error.message);
      const { error } = await supabase.from('report_templates').update({ docx_path: path }).eq('template_key', fa.template_key);
      if (error) throw new Error(error.message);
      await loadBase();
      drawSetup(body);
    } catch (err) {
      btn.disabled = false; btn.textContent = 'Upload';
      msg.className = 'ld-msg err'; msg.textContent = err.message;
    }
  });
}

const setaRow = (s) => `<tr><td><input data-s="body" value="${esc(s.body)}" placeholder="e.g. AgriSETA"></td>
  <td><input data-s="accreditation" value="${esc(s.accreditation)}"></td>
  <td style="width:40px"><button class="btn btn-ghost" data-seta-rm title="Remove this line" aria-label="Remove this line">✕</button></td></tr>`;

function wireSetaRemove(body) {
  body.querySelectorAll('[data-seta-rm]').forEach((b) => { b.onclick = () => b.closest('tr').remove(); });
}

function drawLedger(body, k, faId) {
  const L = LEDGERS[k];
  const card = body.querySelector(`#card-${k}`);
  const rows = S.ledgers[k];
  const progOpts = [['', '—'], ...S.programmes.map((p) => [p.programme_id, p.programme_name])];
  const input = (c, row, i) => {
    const v = row[c.key] ?? '';
    const attrs = `data-row="${i}" data-col="${c.key}"`;
    if (c.type === 'select') return `<select ${attrs}>${opts(c.options || progOpts, v)}</select>`;
    if (c.type === 'number') return `<input type="number" step="any" ${attrs} value="${esc(v)}">`;
    if (c.type === 'date') return `<input type="date" ${attrs} value="${esc(v)}">`;
    if (c.type === 'month') return `<input type="month" ${attrs} value="${esc(v)}">`;
    return `<input ${attrs} value="${esc(v)}">`;
  };
  card.innerHTML = `
    <h2>${esc(L.title)}</h2>
    <p class="ld-sub">${esc(L.sub)}</p>
    <div class="table-wrap"><table class="ld-table rp-ledger">
      <thead><tr>${L.cols.map((c) => `<th${c.wide ? ' class="wide"' : ''}>${esc(c.label)}${c.required ? ' *' : ''}</th>`).join('')}<th></th></tr></thead>
      <tbody>${rows.length ? rows.map((row, i) => `<tr class="${row._del ? 'rp-del' : ''}">${L.cols.map((c) => `<td>${input(c, row, i)}</td>`).join('')}
        <td style="width:40px"><button class="btn btn-ghost" data-rm="${i}" title="${row._del ? 'Keep this line' : 'Remove this line'}" aria-label="${row._del ? 'Keep this line' : 'Remove this line'}">${row._del ? '↺' : '✕'}</button></td></tr>`).join('')
        : `<tr><td colspan="${L.cols.length + 1}" class="ld-small" style="padding:10px">Nothing recorded yet.</td></tr>`}</tbody>
    </table></div>
    <div class="ld-actions">
      <button class="btn btn-ghost" data-add>+ Add a line</button>
      <button class="btn btn-primary" data-save>Save changes</button>
      <span class="ld-msg" data-msg></span>
    </div>`;
  card.querySelectorAll('[data-row]').forEach((inp) => inp.addEventListener('input', () => {
    const row = rows[Number(inp.dataset.row)];
    row[inp.dataset.col] = inp.value;
    row._dirty = true;
  }));
  card.querySelectorAll('[data-rm]').forEach((b) => b.addEventListener('click', () => {
    const row = rows[Number(b.dataset.rm)];
    if (row._new) rows.splice(Number(b.dataset.rm), 1); else row._del = !row._del;
    drawLedger(body, k, faId);
  }));
  card.querySelector('[data-add]').addEventListener('click', () => {
    rows.push({ _new: true, ...(k === 'milestones' ? { sort: rows.length + 1 } : {}) });
    drawLedger(body, k, faId);
    const last = card.querySelector(`[data-row="${rows.length - 1}"]`);
    last?.focus();
  });
  card.querySelector('[data-save]').addEventListener('click', async (e) => {
    const btn = e.currentTarget; const msg = card.querySelector('[data-msg]');
    const toRecord = (row) => {
      const rec = {};
      for (const c of L.cols) {
        let v = row[c.key];
        if (c.type === 'month') {
          const [y, m] = String(v || '').split('-');
          rec.period_year = y ? Number(y) : null; rec.period_month = m ? Number(m) : null;
          continue;
        }
        if (v === '' || v === undefined) v = null;
        if (c.type === 'number' && v != null) v = Number(v);
        rec[c.key] = v;
      }
      if (k === 'placements' && rec.absorbed == null) rec.absorbed = 0;
      if (k === 'milestones') rec.sort = row.sort ?? 0;
      return rec;
    };
    const missing = rows.filter((r) => !r._del && (r._new || r._dirty) && L.cols.some((c) => c.required && !String(r[c.key] ?? '').trim()));
    if (missing.length) { msg.className = 'ld-msg err'; msg.textContent = `Fill in the starred columns on every line (${missing.length} incomplete).`; return; }
    btn.disabled = true; btn.textContent = 'Saving…';
    const errors = [];
    for (const row of rows) {
      let res = null;
      if (row._del && row.id) res = await supabase.from(L.table).delete().eq('id', row.id);
      else if (row._new && !row._del) res = await supabase.from(L.table).insert({ agreement_id: faId, ...toRecord(row) });
      else if (row._dirty && row.id && !row._del) res = await supabase.from(L.table).update(toRecord(row)).eq('id', row.id);
      if (res?.error) errors.push(res.error.message);
    }
    await loadLedgers(faId);
    drawLedger(body, k, faId);
    const m2 = body.querySelector(`#card-${k} [data-msg]`);
    m2.className = `ld-msg ${errors.length ? 'err' : 'ok'}`;
    m2.textContent = errors.length ? `Some lines were not saved: ${[...new Set(errors)].join('; ')}` : 'Saved.';
  });
}

/* ═══════════════════════════ styles ═══════════════════════════ */

const STYLE = `<style>
  #view-reports .ld-tabs { display:flex; gap:4px; margin:0 0 18px; border-bottom:1px solid var(--border); }
  #view-reports .ld-tab { background:none; border:0; border-bottom:2px solid transparent; padding:8px 14px; font:inherit; font-size:.88rem; font-weight:600; color:var(--text-muted); cursor:pointer; }
  #view-reports .ld-tab.on { color:var(--text); border-bottom-color:var(--orange); }
  #view-reports .ld-card { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:18px 20px; margin-bottom:20px; }
  #view-reports .ld-card h2 { margin:0 0 2px; font-size:1.05rem; }
  #view-reports .ld-card h3 { margin:18px 0 8px; font-size:.78rem; text-transform:uppercase; letter-spacing:.04em; }
  #view-reports .ld-sub { font-size:.8rem; color:var(--text-muted); margin:0 0 12px; }
  #view-reports .ld-small { font-size:.74rem; color:var(--text-muted); }
  #view-reports .ld-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(210px,1fr)); gap:10px 14px; }
  #view-reports .ld-grid label, .rp-label { display:flex; flex-direction:column; gap:3px; font-size:.74rem; font-weight:600; color:var(--text-muted); }
  #view-reports input, #view-reports select, #view-reports textarea { padding:7px 9px; border:1px solid var(--border); border-radius:6px; background:var(--surface); color:var(--text); font:inherit; font-size:.85rem; min-width:0; }
  #view-reports textarea { width:100%; resize:vertical; line-height:1.45; }
  #view-reports .ld-table { width:100%; border-collapse:collapse; font-size:.82rem; }
  #view-reports .ld-table th { text-align:left; font-size:.72rem; color:var(--text-muted); font-weight:600; padding:4px 6px; border-bottom:1px solid var(--border); white-space:nowrap; }
  #view-reports .ld-table td { padding:3px 6px; border-bottom:1px solid var(--border); }
  #view-reports .ld-table input, #view-reports .ld-table select { width:100%; padding:5px 7px; }
  #view-reports .rp-ledger th.wide { min-width:220px; } #view-reports .rp-ledger td { min-width:110px; }
  #view-reports .rp-del td input, #view-reports .rp-del td select { text-decoration:line-through; opacity:.5; }
  #view-reports .ld-actions { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-top:16px; padding-top:14px; border-top:1px solid var(--border); }
  #view-reports .ld-msg { font-size:.8rem; }
  #view-reports .ld-msg.ok { color:#15803D; } #view-reports .ld-msg.err { color:var(--red); } #view-reports .ld-msg.warn { color:var(--orange-dark); }
  #view-reports .btn:disabled { opacity:.55; cursor:not-allowed; }
  .rp-back { display:inline-block; font-size:.84rem; font-weight:600; color:var(--orange-dark); text-decoration:none; padding:6px 0; }
  .rp-back:hover { text-decoration:underline; }
  .rp-head-actions { display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
  .rp-progress { height:6px; background:var(--border); border-radius:99px; overflow:hidden; margin:0 0 8px; }
  .rp-progress span { display:block; height:100%; background:#16A34A; transition:width .3s; }
  .rp-notes summary { cursor:pointer; list-style:revert; }
  .rp-sec { background:var(--surface); border:1px solid var(--border); border-left:4px solid var(--border); border-radius:12px; padding:16px 18px; margin-bottom:14px; }
  .rp-sec.rp-draft { border-left-color:#3B82F6; } .rp-sec.rp-approved { border-left-color:#16A34A; } .rp-sec.rp-needs_correction { border-left-color:var(--red); }
  .rp-sec header { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-bottom:10px; }
  .rp-sec h3 { margin:0; font-size:1rem; }
  .rp-num { display:inline-grid; place-items:center; width:24px; height:24px; border-radius:50%; background:var(--surface-2); border:1px solid var(--border); font-size:.74rem; font-weight:700; color:var(--text-muted); }
  .rp-sec-body { font-size:.86rem; line-height:1.55; }
  .rp-sec-actions { display:flex; flex-wrap:wrap; align-items:center; gap:6px; margin-top:12px; padding-top:10px; border-top:1px dashed var(--border); }
  .rp-approve { color:#15803D !important; }
  .rp-panel { margin-top:12px; padding:14px; background:var(--surface-2); border:1px solid var(--border); border-radius:10px; }
  .rp-empty { color:var(--text-muted); font-style:italic; margin:4px 0; }
  .rp-text p { margin:0 0 8px; } .rp-text ul { margin:0 0 8px; padding-left:20px; } .rp-text li { margin:0 0 4px; }
  .rp-kv { border-collapse:collapse; width:100%; margin:4px 0 10px; font-size:.84rem; }
  .rp-kv th { text-align:left; font-weight:500; color:var(--text-muted); padding:5px 10px 5px 0; width:46%; vertical-align:top; border-bottom:1px solid var(--border); white-space:pre-wrap; }
  .rp-kv td { padding:5px 0; border-bottom:1px solid var(--border); font-weight:600; }
  .rp-blank { color:var(--text-light); font-weight:400; }
  .rp-table { border-collapse:collapse; width:100%; font-size:.82rem; margin:4px 0 10px; }
  .rp-table th { font-size:.72rem; color:var(--text-muted); font-weight:600; padding:5px 8px; border-bottom:1px solid var(--border); white-space:nowrap; }
  .rp-table td { padding:5px 8px; border-bottom:1px solid var(--border); vertical-align:top; }
  .rp-table input, .rp-table textarea { width:100%; }
  .rp-note { font-size:.78rem; color:var(--text-muted); margin:4px 0 8px; }
  .rp-warn, .rp-flag { font-size:.8rem; padding:8px 10px; border-radius:8px; margin:0 0 10px; }
  .rp-warn { background:#FFF7ED; color:#9A3412; border:1px solid #FED7AA; }
  .rp-flag { background:#FEF2F2; color:#991B1B; border:1px solid #FECACA; }
  .rp-tiles { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:10px; margin:4px 0 14px; }
  .rp-tile { border:1px solid var(--border); border-radius:10px; padding:10px 12px; }
  .rp-tile span { display:block; font-size:.72rem; color:var(--text-muted); font-weight:600; }
  .rp-tile b { display:block; font-size:1.3rem; margin-top:2px; }
  .rp-tile small { display:block; font-size:.72rem; color:var(--text-muted); }
  .rp-charts { display:grid; grid-template-columns:repeat(auto-fit,minmax(320px,1fr)); gap:14px 22px; margin:6px 0 14px; }
  .rp-chart { margin:0; min-width:0; } .rp-chart.rp-wide { grid-column:1 / -1; } .rp-chart figcaption { font-size:.8rem; font-weight:600; margin-bottom:6px; }
  .rp-busy { display:flex; align-items:center; gap:8px; color:var(--text-muted); font-size:.84rem; }
  .rp-spin { width:14px; height:14px; border:2px solid var(--border); border-top-color:var(--orange); border-radius:50%; animation:rpspin .8s linear infinite; flex:none; }
  @keyframes rpspin { to { transform:rotate(360deg); } }
  .rp-clip { max-width:520px; }
  @media (max-width: 768px) {
    .rp-sec { padding:14px 12px; }
    .rp-kv th { width:52%; white-space:normal; }
    .rp-charts { grid-template-columns:1fr; }
    .rp-head-actions { width:100%; } .rp-head-actions .btn { flex:1 1 auto; }
  }
</style>`;
